import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import { XIcon } from "lucide-react";
import { useLingui } from "@lingui/react/macro";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive overflow-hidden has-[>button.badge-delete]:pl-1.5 has-[>button.badge-delete]:pr-0 has-[>button.badge-delete]:gap-px has-[>button.badge-inner]:pl-0 has-[>button.badge-inner]:gap-0",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        purple:
          "border-purple-400 bg-purple-50 text-foreground [a&]:hover:bg-purple-500/90",
        red: "border-red-300 bg-red-50 text-foreground [a&]:hover:bg-red-500/90",
        cyan: "border-cyan-300 bg-cyan-50 text-foreground [a&]:hover:bg-cyan-500/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
      shape: {
        round: "rounded-full",
        square: "rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  children,
  onDelete,
  onClick,
  onDoubleClick,
  onMouseDown,
  shape = "round",
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    onDelete?: () => void;
  }) {
  const { t } = useLingui();

  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, shape }), className)}
      {...(onClick ? props : { onDoubleClick, onMouseDown, ...props })}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          onMouseDown={onMouseDown}
          className="badge-inner pl-2"
        >
          {children}
        </button>
      ) : (
        children
      )}
      {onDelete && (
        <Button
          aria-label={t`Ta bort`}
          title={t`Ta bort`}
          size="icon-xs"
          variant="ghost"
          onClick={onDelete}
          className="badge-delete transition-none pl-1 pr-2 w-min"
        >
          <XIcon />
        </Button>
      )}
    </Comp>
  );
}

export { Badge, badgeVariants };
